import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Label,
  LabelList,
  Text,
} from "recharts";
import { useIsMobileWidth, useWindowDimensions } from "@src/utils/mediaQuery";
import { getTextLines } from "./graphUtils";

const CustomizedAxisTick = (props) => {
  const { x, y, payload } = props;

  const lineCount = getTextLines(payload.value, 75);
  return (
    <g key={`key-${x}-${y}`} transform={`translate(${x},${y})`} width={75}>
      <Text
        x={0}
        y={0 + lineCount * 10}
        dy={16}
        width={60}
        fontSize={"1rem !important"}
        textAnchor="middle"
        fill="black"
      >
        {payload.value}
      </Text>
    </g>
  );
};

const splitIntoSentences = (message) => {
  const trimmedMessage = message.trim();

  // Check if the string contains any punctuation
  if (!/[\.!\?]/.test(trimmedMessage)) {
    return [trimmedMessage];
  }
  const sentences = trimmedMessage.match(/[^\.!\?]+[\.!\?]+/g);
  return sentences ? sentences : [];
};

const CustomizedRefLabel = (payload) => {
  const { value, viewBox, fill } = payload;
  const { x, y, width } = viewBox;
  const lineCount = value ? getTextLines(value, 90) : 2;

  return (
    <g key={`key-${x}-${y}`} transform={`translate(${x},${y})`} width={90}>
      <Text
        x={width}
        y={lineCount * 5}
        width={90}
        fontSize={"1rem !important"}
        textAnchor="right"
        fill={fill}
      >
        {value}
      </Text>
    </g>
  );
};

const EmptyBarGraph = (props) => {
  const {
    data,
    threshold,
    thresholdLabel,
    domain,
    mobileDomain,
    refLineColour,
    axisLabel,
    hideNum = false,
    hideYLine = false,
    ticks = undefined,
    message = "No data to display",
    messageColour = "grey",
    hideYAxisOnDesktop = false,
  } = props;

  const belowThresholdAnimationDuration = 500;
  const mobileLayout = useIsMobileWidth();
  const { width } = useWindowDimensions();
  const margins = {
    top: 50,
    right: mobileLayout ? 90 : 5,
    bottom: 50,
    left: 10,
  };
  const barSize = 40;

  const sentences = splitIntoSentences(message);

  return (
    <div id={"signol-chart-container"}>
      {mobileLayout ? (
        <div className="relative">
          <div
            id={"message-wrapper"}
            className="flex flex-col items-center justify-center absolute inset-0"
          >
            <div style={{ backgroundColor: messageColour }} className="p-4">
              {sentences.map((s, i) => (
                <div
                  key={`sentence-${i}`}
                  id={"message-string"}
                  className="text-neutral-700 text-base"
                >
                  {s}
                </div>
              ))}
            </div>
          </div>
          <div>
            <ResponsiveContainer height={width * 1.4} width={"100%"}>
              <BarChart
                with={"100%"}
                data={data}
                barCategoryGap={10}
                margin={margins}
              >
                <YAxis
                  domain={mobileDomain ? mobileDomain : domain}
                  axisLine={!hideYLine}
                  tickLine={!hideYLine}
                  tick={{ fill: hideNum ? "none" : "black", fontSize: "1rem" }}
                  ticks={ticks?.length > 0 ? ticks : undefined}
                >
                  <Label
                    value={axisLabel}
                    angle="-90"
                    position="insidemiddle"
                    fill="black"
                  />
                </YAxis>
                <XAxis
                  type="category"
                  dataKey={!!data[0].mobileName ? "mobileName" : "name"}
                  angle={"-35"}
                  tick={<CustomizedAxisTick />}
                  tickLine={false}
                  minTickGap={1}
                  height={100}
                />
                <ReferenceLine
                  label={
                    <CustomizedRefLabel
                      value={thresholdLabel}
                      fill={refLineColour}
                    />
                  }
                  y={threshold}
                  stroke={refLineColour}
                  strokeDasharray="6 6"
                  strokeWidth="2"
                />
                <Bar
                  dataKey="beforeThreshold"
                  stackId="a"
                  maxBarSize={barSize}
                  animationDuration={belowThresholdAnimationDuration}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`empty-bar1-cell-${entry.name}-${index}`}
                      fill={entry.underThresholdColour}
                    />
                  ))}
                </Bar>
                <Bar
                  dataKey="afterThreshold"
                  stackId="a"
                  animationDuration={belowThresholdAnimationDuration}
                  animationBegin={belowThresholdAnimationDuration}
                  maxBarSize={barSize}
                  barCategoryGap={50}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`empty-bar2-cell-${entry.name}-${index}`}
                      fill={entry.overThresholdColour}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {!!data[0].mobileName && (
            <div className={"flex flex-col pb-12 px-4"}>
              {data.map((item) => (
                <p key={`legend-${item.name}`} color="green !important">
                  {item.mobileName}: {item.name}
                </p>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="relative">
          <div
            id={"message-wrapper"}
            className="flex flex-col items-center justify-center absolute inset-0"
          >
            <div style={{ backgroundColor: messageColour }} className="p-4">
              {message}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={150 * data.length + 100}>
            <BarChart
              data={data}
              barCategoryGap={2}
              barGap={1}
              margin={margins}
              layout="vertical"
            >
              <YAxis
                type="category"
                dataKey="name"
                tickLine={false}
                width={100}
                tick={{ fill: "black", fontSize: "2rem !important" }}
                axisLine={!hideYAxisOnDesktop}
              />
              <XAxis
                type="number"
                domain={domain}
                tickCount={ticks?.length > 0 ? undefined : 6}
                tick={{ fill: hideNum ? "none" : "black", fontSize: "1rem" }}
                ticks={ticks?.length > 0 ? ticks : undefined}
              >
                <Label value={axisLabel} fill="black" position="bottom" />
              </XAxis>
              <ReferenceLine
                label={{
                  position: "top",
                  value: thresholdLabel,
                  width: 160,
                  fill: refLineColour,
                }}
                x={threshold}
                stroke={refLineColour}
                strokeDasharray="6 6"
                strokeWidth="3"
              />
              <Bar
                dataKey="beforeThreshold"
                stackId="a"
                maxBarSize={barSize}
                animationDuration={belowThresholdAnimationDuration}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.underThresholdColour}
                  />
                ))}
                <label value="test" />
              </Bar>
              <Bar
                dataKey="afterThreshold"
                stackId="a"
                animationDuration={belowThresholdAnimationDuration}
                animationBegin={belowThresholdAnimationDuration}
                maxBarSize={barSize}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.overThresholdColour}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default EmptyBarGraph;
